import React from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { MdHistory } from "react-icons/md";

import styles from "./Order.module.scss";
import { useOrder } from "./useOrder";
import { PageHeader } from "../../../common/components/pageHeader/PageHeader";
import { EmptyState } from "../../../common/components/emptyState/EmptyState";
import { StatusChip } from "../../../common/components/statusChip/StatusChip";
import {
  getOrderStatusText,
  getStatusTextColor,
} from "../../../common/utils/functions/enumToText";
import { OrderStatus } from "../../../common/types/enums";
import { OrderItem } from "./OrderItem";
import { ORDERS_ROUTE, PROFILE_ROUTE } from "../../../common/utils/constants";

const STATUSES = [
  OrderStatus.NEW,
  OrderStatus.PREPARING,
  OrderStatus.DELIVERING,
  OrderStatus.DELIVERED,
  OrderStatus.PICKED_UP,
  OrderStatus.CANCELLED,
];

export const Order: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { orders, status, ready, isFetchingMore, isEmpty, lastItemRef } =
    useOrder();

  const handleStatusSelect = (value: OrderStatus | null) => {
    const params = new URLSearchParams(searchParams);
    if (value === null) {
      params.delete("status");
    } else {
      params.set("status", String(value));
    }
    setSearchParams(params);
  };

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>Заказы — ЛОГМОЛЛ</title>
      </Helmet>

      <PageHeader title="История заказов" backRoute={PROFILE_ROUTE} />

      <div className={styles.Filters}>
        <StatusChip
          label="Все"
          isActive={status === undefined}
          onClick={() => handleStatusSelect(null)}
          compact
        />
        {STATUSES.map((s) => (
          <StatusChip
            key={s}
            label={getOrderStatusText(s)}
            color={getStatusTextColor(s)}
            isActive={status === s}
            onClick={() => handleStatusSelect(status === s ? null : s)}
            compact
          />
        ))}
      </div>

      {!ready ? (
        <div className={styles.Loader}>Загрузка...</div>
      ) : isEmpty ? (
        <EmptyState
          icon={MdHistory}
          title="Заказов пока нет"
          description="Оформите первый заказ в каталоге."
        />
      ) : (
        <>
          <div className={styles.List}>
            {orders.map((order, index) => (
              <OrderItem
                key={order.id}
                order={order}
                lastItemRef={
                  index === orders.length - 1 ? lastItemRef : undefined
                }
              />
            ))}
          </div>

          {isFetchingMore && <div className={styles.Loader}>Загрузка...</div>}
        </>
      )}
    </div>
  );
};